import React from 'react';
import { WebsiteTab } from './WebsiteTab';
import { SavedPropertiesTab } from './SavedPropertiesTab';
import { HomeValuationsTab } from './HomeValuationsTab';
import { useContactWebActivityGQL } from '../../../../hooks/graphql/contacts/useContactWebActivityGQL';
import { useContactSavedPropertiesGQL } from '../../../../hooks/graphql/contacts/useContactSavedPropertiesGQL';
import { useContactValuationsGQL } from '../../../../hooks/graphql/contacts/useContactValuationsGQL';
import { isPreviewContactId, PREVIEW_WEB_ACTIVITY, PREVIEW_SAVED_PROPERTIES, PREVIEW_VALUATIONS } from '../../../../pages/previewMockData';

interface WebsiteActivityTabProps {
  contactId: string;
  firebaseUid?: string;
  onContactUpdate?: () => void | Promise<void>;
}

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex flex-col min-w-0 px-4 py-3.5 gap-1">
    <span className="text-2xl font-bold text-foreground tabular-nums">
      {value ?? 0}
    </span>
    <span className="text-[11px] text-muted-foreground uppercase tracking-wider">
      {label}
    </span>
  </div>
);

/**
 * Website activity — stats row on top (times visited / saved properties
 * / searches), then the three live tab tables stacked on one scrollable
 * page. Each inner tab keeps its own ActivityTableHeader title and
 * filter cluster, so this wrapper stays chrome-free.
 */
export const WebsiteActivityTab: React.FC<WebsiteActivityTabProps> = ({
  contactId,
  firebaseUid,
  onContactUpdate,
}) => {
  const isPreview = isPreviewContactId(contactId);

  // Stat queries. Apollo caches by variables so re-renders don't re-fetch.
  // - `allVisits` has no eventTypes filter → counts every web event ever
  //   (page visits / property views / searches / lead captures / etc).
  // - `propertyViews` and `searches` are filtered subsets.
  const allVisits = useContactWebActivityGQL(isPreview ? '' : contactId);
  const propertyViews = useContactWebActivityGQL(isPreview ? '' : contactId, ['property_view']);
  const searches = useContactWebActivityGQL(isPreview ? '' : contactId, ['search']);
  const saved = useContactSavedPropertiesGQL(isPreview ? '' : contactId);
  const valuations = useContactValuationsGQL(isPreview ? '' : contactId);

  const visitsTotal = isPreview ? PREVIEW_WEB_ACTIVITY.length : allVisits.total;
  const propertyViewsTotal = isPreview
    ? PREVIEW_WEB_ACTIVITY.filter((e: { eventType: string }) => e.eventType === 'property_view').length
    : propertyViews.total;
  const searchesTotal = isPreview
    ? PREVIEW_WEB_ACTIVITY.filter((e: { eventType: string }) => e.eventType === 'search').length
    : searches.total;
  const savedTotal = isPreview ? PREVIEW_SAVED_PROPERTIES.length : saved.total;
  const valuationsTotal = isPreview ? PREVIEW_VALUATIONS.length : valuations.total;

  return (
    <div className="flex flex-col flex-grow min-h-0 overflow-y-auto scrollbar-custom -mx-3 md:-mx-4 lg:-mx-5 px-3 md:px-4 lg:px-5 pb-4">
      <div className="flex flex-col gap-6">
        {/* Stats row — one bordered container, 5 plain number+label cells
            side by side. Times visited = every web event; Property views
            and Searches are the filtered subsets. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 rounded-xl border border-foreground/30 divide-x divide-foreground/15 bg-white dark:bg-card flex-shrink-0 shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.35)]">
          <Stat value={visitsTotal} label="Times visited" />
          <Stat value={propertyViewsTotal} label="Property views" />
          <Stat value={savedTotal} label="Saved properties" />
          <Stat value={searchesTotal} label="Searches" />
          <Stat value={valuationsTotal} label="Home valuations" />
        </div>

        <WebsiteTab contactId={contactId} firebaseUid={firebaseUid} isActive={true} />
        <SavedPropertiesTab contactId={contactId} isActive={true} />
        <HomeValuationsTab
          contactId={contactId}
          firebaseUid={firebaseUid}
          isActive={true}
          onContactUpdate={onContactUpdate}
        />
      </div>
    </div>
  );
};
